import { getAccessToken, SUPABASE_ANON } from './session';

// One PostgREST caller for every per-member feature.
//
// Extracted from lib/pass.js, where this logic was written and proven. It had
// been copied three ways with *different* retry behaviour (6 attempts here, 3 in
// volunteer.js, none in vms.js), which meant the fragility of a call depended on
// which file it happened to live in.
//
// The retry is not optional politeness: PostgREST scales to zero, so the first
// call after an idle period can return 503/PGRST002 while the schema cache warms.
// A member tapping their magic link on a cold backend must still land on their
// badge rather than an error.

export const POSTGREST_URL = (process.env.REACT_APP_POSTGREST_URL
  || 'https://yuftrqcahapyrblxvkns.supabase.co/rest/v1').replace(/\/$/, '');

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Call a PostgREST function. Returns parsed JSON, or throws on a definite
 * (non-cold) HTTP/auth error. `err.noauth` marks "not signed in";
 * `err.status` carries the HTTP status for anything the caller wants to branch on.
 */
export async function rpc(fn, body) {
  const token = await getAccessToken();
  if (!token) { const e = new Error('not_signed_in'); e.noauth = true; throw e; }
  let lastErr;
  for (let attempt = 0; attempt < 6; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await fetch(`${POSTGREST_URL}/rpc/${fn}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', apikey: SUPABASE_ANON, authorization: `Bearer ${token}` },
        body: JSON.stringify(body || {}),
      });
      let data = null;
      // eslint-disable-next-line no-await-in-loop
      try { data = await res.json(); } catch { data = null; }
      if (res.ok) return data;
      const cold = res.status === 502 || res.status === 503
        || (data && (data.code === 'PGRST002' || /schema cache/i.test(data.message || '')));
      // eslint-disable-next-line no-await-in-loop
      if (cold && attempt < 5) { await sleep(1200 + attempt * 1200); continue; }
      const e = new Error((data && data.message) || `HTTP ${res.status}`);
      e.status = res.status;
      e.body = data;
      throw e;
    } catch (err) {
      lastErr = err;
      if (err && (err.status || err.noauth)) throw err; // definite error — don't loop
      // eslint-disable-next-line no-await-in-loop
      if (attempt < 5) { await sleep(1200 + attempt * 1200); continue; } // blip during warmup
      throw lastErr;
    }
  }
  throw lastErr || new Error('request_failed');
}

/**
 * For RPCs that may not be deployed yet. Returns `fallback` instead of throwing
 * when the function is missing (404/400 from PostgREST) or the caller is signed
 * out, and logs the gap once. Features that depend on P7's migrations use this
 * so the app degrades quietly instead of erroring in front of a member.
 */
const warned = new Set();

export async function rpcOptional(fn, body, fallback = null) {
  try {
    return await rpc(fn, body);
  } catch (err) {
    if (err?.noauth) return fallback;
    if (err?.status === 404 || err?.status === 400) {
      if (!warned.has(fn)) {
        warned.add(fn);
        // eslint-disable-next-line no-console
        console.info(`[rpc] ${fn} is not deployed yet — degrading gracefully.`);
      }
      return fallback;
    }
    return fallback;
  }
}
